import React from 'react';
import { Check, Info } from 'lucide-react';

export default function BrandingDomainTab({ form, update }) {
  const customDomain = form.custom_domain || 'aidasapps.com';
  
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      
      {/* Domain Settings Section */}
      <div>
        <h3 className="font-bold text-gray-900 dark:text-white text-lg">Domain Settings</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Configure your custom domain and important links.</p>
        
        {/* Custom Domain */}
        <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm max-w-3xl">
           <h4 className="font-semibold text-gray-900 dark:text-white">Custom Domain</h4>
           <p className="text-sm text-gray-500 mt-1 mb-4">Use your own domain name for your application.</p>
           
           <div className="relative mb-2 max-w-xl">
             <input 
               type="text" 
               value={customDomain} 
               onChange={(e) => update('custom_domain', e.target.value)}
               className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none pr-32"
               placeholder="app.yourcompany.com"
             />
             <div className="absolute right-2 top-1.5 bg-green-50 text-green-600 border border-green-200 px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Connected
             </div>
           </div>
           
           {customDomain && (
             <div className="flex items-center gap-1.5 text-sm text-green-600 font-medium ml-1">
                <Check className="w-4 h-4" /> 
                <span>Your app will be available at: https://{customDomain}</span>
             </div>
           )}
        </div>
      </div>

      {/* Legal & Support Links Section */}
      <div>
        <h3 className="font-bold text-gray-900 dark:text-white text-lg mt-8 mb-4">Legal & Support Links</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 -mt-3">Add links to your legal pages and support contact.</p>
        
        <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm">
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              
              <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Privacy Policy URL</label>
                 <input 
                   type="text" 
                   value={form.privacy_policy_url || 'https://aidasapps.com/privacy'} 
                   onChange={(e) => update('privacy_policy_url', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                 />
              </div>

              <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Terms of Service URL</label>
                 <input 
                   type="text" 
                   value={form.terms_of_service_url || 'https://aidasapps.com/terms'} 
                   onChange={(e) => update('terms_of_service_url', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                 />
              </div>

              <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Support Email</label>
                 <input 
                   type="email" 
                   value={form.support_email || 'support@aidasapps.com'} 
                   onChange={(e) => update('support_email', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                 />
              </div>

              <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Support URL</label>
                 <input 
                   type="text" 
                   value={form.support_url || 'https://aidasapps.com/support'} 
                   onChange={(e) => update('support_url', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                 />
              </div>

              <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cookie Policy URL <span className="text-gray-400 font-normal">(Optional)</span></label>
                 <input 
                   type="text" 
                   value={form.cookie_policy_url || 'https://aidasapps.com/cookie-policy'} 
                   onChange={(e) => update('cookie_policy_url', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                 />
              </div>

              <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Processing Agreement URL <span className="text-gray-400 font-normal">(Optional)</span></label>
                 <input 
                   type="text" 
                   value={form.dpa_url || 'https://aidasapps.com/dpa'} 
                   onChange={(e) => update('dpa_url', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                 />
              </div>

           </div>

           <div className="bg-[#f8fafc] dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 p-4 rounded-lg flex items-start gap-3 text-sm border border-gray-100 dark:border-gray-700 mt-2">
             <div className="bg-blue-600 text-white rounded-full p-0.5 mt-0.5 shrink-0">
                 <Info className="w-3.5 h-3.5" />
             </div>
             <p>These links will be accessible in the footer of your platform and in communications where applicable.</p>
           </div>
        </div>
      </div>

    </div>
  );
}
